import { useEffect, useState, type RefObject } from 'react';

export interface UseVirtualRowsOptions {
  count: number;
  rowHeight: number;
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  overscan?: number;
}

export interface VirtualWindow {
  start: number;
  end: number;
  padTop: number;
  padBottom: number;
}

/**
 * Fixed-height windowing over a scroll container.
 *
 * Rows are all the same height by construction (see `ROW_HEIGHT`), which turns
 * the visible range into arithmetic — no measuring, no library, no layout
 * thrash. Scroll handling is coalesced into an animation frame so a fast
 * flick costs one render per frame instead of one per scroll event.
 */
export function useVirtualRows({
  count,
  rowHeight,
  containerRef,
  enabled,
  overscan = 8,
}: UseVirtualRowsOptions): VirtualWindow {
  const [range, setRange] = useState({ scrollTop: 0, viewport: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      setRange((prev) =>
        prev.scrollTop === container.scrollTop && prev.viewport === container.clientHeight
          ? prev
          : { scrollTop: container.scrollTop, viewport: container.clientHeight },
      );
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    measure();
    container.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(container);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      container.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, [containerRef, enabled, count, rowHeight]);

  if (!enabled) return { start: 0, end: count, padTop: 0, padBottom: 0 };

  const viewport = range.viewport || 600;
  const first = Math.max(0, Math.floor(range.scrollTop / rowHeight) - overscan);
  const visible = Math.ceil(viewport / rowHeight) + overscan * 2;
  const last = Math.min(count, first + visible);

  return {
    start: first,
    end: last,
    padTop: first * rowHeight,
    padBottom: Math.max(0, (count - last) * rowHeight),
  };
}
